import '@/styles/index.scss';

import { App } from '@/App';
import { useAppLaunchParams } from '@/hooks/useAppLaunchParams';
import { ContextStore, createStore } from '@/store';
import { initApi, setupAxiosRetry } from '@/utils/api';
import { handleTokenAuthentication } from '@/utils/auth';
import { initI18n } from '@/utils/initI18n';
import { initSentry } from '@/utils/initSentry';
import { initTelegramApp } from '@/utils/initTelegramApp';
import { isTelegramApp } from '@/utils/isTelegramApp';
import { loadScript } from '@/utils/loadScript';

import { GoogleOAuthProvider } from '@react-oauth/google';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import ReactDOM from 'react-dom';
import { YMInitializer } from 'react-yandex-metrika';

declare global {
  interface Telegram {
    initData: string;
    initDataUnsafe: string;
    platform: string;
  }

  interface Window {
    Telegram?: Telegram;
  }
}

if (!isTelegramApp()) {
  import('@/styles/web.scss');
}

runApp();

async function runApp() {
  const loadingMessages = [
    'Loading Pollo...',
    'Encrypting connection...',
    'Initializing systems...',
    'Preparing your experience...',
    'Almost there...',
  ];

  const loadingElement = document.createElement('div');
  loadingElement.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 14px;
    color: #fff;
    text-align: center;
    opacity: 0.4;
  `;
  document.body.appendChild(loadingElement);

  let messageIndex = 0;
  const updateLoadingMessage = () => {
    loadingElement.textContent = loadingMessages[messageIndex];
    messageIndex = (messageIndex + 1) % loadingMessages.length;
  };

  setInterval(updateLoadingMessage, 2000);
  updateLoadingMessage();

  const startTime = performance.now();
  console.log('[PERF] 🚀 App initialization started');

  if (import.meta.env.MODE !== 'development') {
    const sentryStart = performance.now();
    initSentry();
    console.log(
      `[PERF] ✅ Sentry initialized in ${(performance.now() - sentryStart).toFixed(2)}ms`,
    );
  }

  const telegramStart = performance.now();
  const { startParams, shouldSupportFullscreen } = isTelegramApp()
    ? await initTelegramApp()
    : { startParams: useAppLaunchParams(), shouldSupportFullscreen: false };
  console.log(
    `[PERF] ✅ Telegram/Launch params initialized in ${(performance.now() - telegramStart).toFixed(2)}ms`,
  );

  const i18nStart = performance.now();
  await initI18n();
  console.log(
    `[PERF] ✅ i18n initialized in ${(performance.now() - i18nStart).toFixed(2)}ms`,
  );

  const storeStart = performance.now();
  const store = createStore({ startParams, shouldSupportFullscreen });
  console.log(
    `[PERF] ✅ Store created in ${(performance.now() - storeStart).toFixed(2)}ms`,
  );

  console.log('search', window.location.search);

  // add adsonar (non-blocking)
  const adsonarStart = performance.now();
  const appId = import.meta.env.VITE_ADSONAR_APP_ID;
  const isDebug = import.meta.env.VITE_ADSONAR_IS_DEBUG === 'true';
  const sonarUrl = `https://static.sonartech.io/lib/1.0.0/sonar.js?appId=${appId}&isDebug=${isDebug}`;
  if (appId) {
    loadScript({ src: sonarUrl });
    console.log(
      `[PERF] ⏳ Adsonar script loading started in ${(performance.now() - adsonarStart).toFixed(2)}ms`,
    );
  }

  const apiStart = performance.now();
  initApi();
  console.log(
    `[PERF] ✅ API initialized in ${(performance.now() - apiStart).toFixed(2)}ms`,
  );

  const authStart = performance.now();
  await handleTokenAuthentication(store);
  console.log(
    `[PERF] ✅ Token authentication completed in ${(performance.now() - authStart).toFixed(2)}ms`,
  );

  const retryStart = performance.now();
  setupAxiosRetry(store);
  console.log(
    `[PERF] ✅ Axios retry setup completed in ${(performance.now() - retryStart).toFixed(2)}ms`,
  );

  console.log(
    `[PERF] 🎉 Total initialization time: ${(performance.now() - startTime).toFixed(2)}ms`,
  );

  if (loadingElement?.parentNode) {
    loadingElement.parentNode.removeChild(loadingElement);
  }

  // eslint-disable-next-line react/no-deprecated
  ReactDOM.render(
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID}>
      <ContextStore.Provider value={store}>
        <TonConnectUIProvider manifestUrl="https://pollometa.fra1.cdn.digitaloceanspaces.com/tonconnect-manifest.json">
          {!!Number(import.meta.env.VITE_YANDEX_METRIKA_ID) && (
            <YMInitializer
              accounts={[Number(import.meta.env.VITE_YANDEX_METRIKA_ID)]}
              options={{
                webvisor:
                  import.meta.env.VITE_YANDEX_METRIKA_ENABLE_WEBVISOR ===
                  'true',
              }}
              version="2"
            />
          )}
          <App />
        </TonConnectUIProvider>
      </ContextStore.Provider>
    </GoogleOAuthProvider>,
    document.getElementById('root')!,
  );
}
